import { useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { useHasQbo } from "../../../../utils/utils";
import { convertTlsToTable, getDocumentName } from "./utils";
import { groupBy } from "lodash";
import { useLocation } from "react-router-dom";

export function useTransactions(onlyIncluded = true) {
  const hasQbo = useHasQbo();

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );

  const { tls, accountChunkLabels } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { tls: transactions } = tls;

  return convertTlsToTable(
    onlyIncluded
      ? (transactions || []).filter((x) => !!x.included)
      : transactions || [],
    jobDocs || [],
    job,
    hasQbo,
    accountChunkLabels
  );
}

export function useShowOneOff() {
  const { user } = useSelector((state: ReduxState) => state.User);
  const { subscription } = useSelector(
    (state: ReduxState) => state.Subscription
  );

  return (
    user?.document_frequency === "One Time Usage" ||
    ["Personal Use", "Business Owner / Founder"].includes(user?.segment || "")
  );
}

export function useTransactionsAutomaticMode(documentId, onlyIncluded = true) {
  const { tls } = useSelector((state: ReduxState) => state.TransactionManager);

  const data = tls.tls
    .filter((t) => t.documentId.toString() === documentId)
    .map((t) => {
      try {
        const row = JSON.parse(t.row as any);
        const obj = {};
        row.forEach((value, index) => {
          obj[index] = value;
        });
        return obj;
      } catch (err) {
        return {};
      }
    });

  return data;
}

export function useTotalsByAccount() {
  // account => chunk => fieldName
  const totalsByAccount = {};

  const { totalsByChunk } = useSelector(
    (state: ReduxState) => state.Reconciler
  );

  for (const account in totalsByChunk) {
    if (!(account in totalsByAccount)) {
      totalsByAccount[account] = {};
    }
    for (const documentId in totalsByChunk[account]) {
      for (const chunk in totalsByChunk[account][documentId]) {
        for (const name in totalsByChunk[account][documentId][chunk]) {
          if (!(name in totalsByAccount[account])) {
            totalsByAccount[account][name] = {
              numCredits: 0,
              numPayments: 0,
              totalCredits: 0,
              totalPayments: 0,
            };
          }
          totalsByAccount[account][name] = {
            numCredits:
              totalsByAccount[account][name].numCredits +
              totalsByChunk[account][documentId][chunk][name].numCredits,
            numPayments:
              totalsByAccount[account][name].numPayments +
              totalsByChunk[account][documentId][chunk][name].numPayments,

            totalCredits:
              totalsByAccount[account][name].totalCredits +
              parseFloat(
                totalsByChunk[account][documentId][chunk][name].totalCredits
              ),
            totalPayments:
              totalsByAccount[account][name].totalPayments +
              parseFloat(
                totalsByChunk[account][documentId][chunk][name].totalPayments
              ),
          };
        }
      }
    }

    return totalsByAccount;
  }
}

interface TransactionLine {
  documentId: string;
  accountName?: string;
  chunk?: string;
  included?: boolean;
  row?: string | any[];
}

export function useChunkData() {
  const { tls, mfs } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );

  const { totalsByChunk } = useSelector(
    (state: ReduxState) => state.Reconciler
  );

  // Group MFS data for easy lookup
  const grouped = groupBy(
    mfs.mfs || [],
    (item: any) => `${item?.documentId}-${item?.accountName}-${item?.chunk}`
  );

  // Get all chunks from totalsByChunk
  const chunks: Array<{
    accountName: string;
    documentId: string;
    chunk: string;
  }> = [];
  for (const accountName in totalsByChunk) {
    for (const documentId in totalsByChunk[accountName]) {
      for (const chunk in totalsByChunk[accountName][documentId]) {
        chunks.push({ accountName, documentId, chunk });
      }
    }
  }

  // Process all chunks from totalsByChunk
  return chunks.map(({ accountName, documentId, chunk }) => {
    const key = `${documentId}-${accountName}-${chunk}`;
    const fields = grouped[key] || [];

    const helper = (x: any[], isDate = false) => {
      if (x.length > 0) return x[0].value;
      return isDate ? "Unknown date" : "";
    };

    const startBalance = fields.filter(
      (x) => (x as any)?.name === "startBalance"
    );
    const endBalance = fields.filter((x) => (x as any)?.name === "endBalance");
    const startDate = fields.filter((x) => (x as any)?.name === "startDate");
    const endDate = fields.filter((x) => (x as any)?.name === "endDate");
    const isReconciled = fields.filter(
      (x) => (x as any)?.name === "isReconciled"
    );

    let totals = {} as any;
    if (
      accountName in totalsByChunk &&
      documentId in totalsByChunk[accountName] &&
      chunk in totalsByChunk[accountName][documentId] &&
      "Transactions" in totalsByChunk[accountName][documentId][chunk]
    ) {
      totals = totalsByChunk[accountName][documentId][chunk].Transactions;
    }

    return {
      documentId,
      documentName: getDocumentName(jobDocs || [], documentId),
      accountName,
      chunk,
      startBalance: helper(startBalance),
      endBalance: helper(endBalance),
      startDate: helper(startDate, true),
      endDate: helper(endDate, true),
      isReconciled: helper(isReconciled),
      credits: `${totals?.totalCredits || 0} (${totals?.numCredits || 0})`,
      debits: `${totals?.totalPayments || 0} (${totals?.numPayments || 0})`,
    };
  });
}
